import {
  CreateCargoClaimCommandInput,
  SiteDetailsInput,
  UpdateCargoClaimAttachmentsCommandInput,
  UpdateCargoClaimCommandInput,
} from "@amzn/ttechclaimintakeservice-client";
import {
  BUSINESS_DETAILS_CONSTANTS,
  CLAIM_DETAILS_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  INCIDENT_DETAILS_CONSTANTS,
  SHIPMENT_DETAILS_CONSTANTS,
} from "src/forms/Cargo/constants";
import { FormStateType } from "src/forms/types";
import { buildAttachments } from "../../forms/FileUpload/FileUploadUtilities";
import { formatCurrencyStringToNumber } from "../../app/Cargo/utils";
import { CargoClaimAdditionalFields } from "../../app/Cargo/types";

export const createCargoClaimModelBuilder = (
  formState: FormStateType,
  additionalFields: CargoClaimAdditionalFields
): CreateCargoClaimCommandInput => {
  const companyAddress: SiteDetailsInput = {
    addressLine1:
      formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_LINE_1]?.value,
    addressLine2:
      formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_LINE_2]?.value,
    city: formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_CITY]?.value,
    state: formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_STATE]?.value,
    zipCode:
      formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_ZIP_CODE]?.value,
  };
  const createCargoClaimModel: CreateCargoClaimCommandInput = {
    amazonClaimReferenceId: additionalFields.amazonClaimReferenceId,

    // Claim Reporter information
    claimReporterFirstName: additionalFields?.user?.givenName || "",
    claimReporterLastName: additionalFields?.user?.familyName || "",
    claimReporterAlias: additionalFields?.user?.id || "",
    claimReporterJobDescription: additionalFields?.user?.jobDescription || "",
    claimReporterEmailAddress: additionalFields?.user?.email || "",

    //PAGE 1 BUSINESS DETAILS
    businessUnit:
      formState[BUSINESS_DETAILS_CONSTANTS.BUSINESS_UNIT]?.value?.value,
    vertical: formState[BUSINESS_DETAILS_CONSTANTS.VERTICAL]?.value?.value,
    companyName: formState[BUSINESS_DETAILS_CONSTANTS.COMPANY_NAME]?.value,
    pointOfContactName:
      formState[BUSINESS_DETAILS_CONSTANTS.POINT_OF_CONTACT_NAME]?.value,
    pointOfContactEmailAddress:
      formState[BUSINESS_DETAILS_CONSTANTS.POINT_OF_CONTACT_EMAIL_ADDRESS]
        ?.value,
    additionalContactEmailAddresses: [
      formState[BUSINESS_DETAILS_CONSTANTS.ADDITIONAL_CONTACT_EMAIL_ADDRESS_0]
        ?.value,
      formState[BUSINESS_DETAILS_CONSTANTS.ADDITIONAL_CONTACT_EMAIL_ADDRESS_1]
        ?.value,
    ],
    companyAddress,
    countryId: formState[BUSINESS_DETAILS_CONSTANTS.COUNTRY_ID]?.value?.value,

    //PAGE 2 SHIPMENT DETAILS
    carrierId: formState[SHIPMENT_DETAILS_CONSTANTS.CARRIER_ID]?.value?.value,
    carrierName: formState[SHIPMENT_DETAILS_CONSTANTS.CARRIER_NAME]?.value,
    standardCarrierAlphaCode:
      formState[SHIPMENT_DETAILS_CONSTANTS.STANDARD_CARRIER_ALPHA_CODE]?.value
        ?.value,
    carrierEmailAddress:
      formState[SHIPMENT_DETAILS_CONSTANTS.CARRIER_EMAIL_ADDRESS]?.value,
    carrierType:
      formState[SHIPMENT_DETAILS_CONSTANTS.CARRIER_TYPE]?.value?.value,
    transportationMode:
      formState[SHIPMENT_DETAILS_CONSTANTS.TRANSPORTATION_MODE]?.value?.value,
    trailerType:
      formState[SHIPMENT_DETAILS_CONSTANTS.TRAILER_TYPE]?.value?.value,
    cmrReserved:
      formState[SHIPMENT_DETAILS_CONSTANTS.CMR_RESERVED]?.value?.value ??
      formState[SHIPMENT_DETAILS_CONSTANTS.CMR_RESERVED]?.value,
    pncTimelineMet:
      formState[SHIPMENT_DETAILS_CONSTANTS.PNC_TIMELINE_MET]?.value?.value ??
      formState[SHIPMENT_DETAILS_CONSTANTS.PNC_TIMELINE_MET]?.value,
    vridLoadId: formState[SHIPMENT_DETAILS_CONSTANTS.VRID_LOAD_ID]?.value,
    amazonOrderNumber:
      formState[SHIPMENT_DETAILS_CONSTANTS.AMAZON_ORDER_NUMBER]?.value,
    modeOfTransport:
      formState[SHIPMENT_DETAILS_CONSTANTS.MODE_OF_TRANSPORT]?.value?.value,
    flightNumber: formState[SHIPMENT_DETAILS_CONSTANTS.FLIGHT_NUMBER]?.value,
    vesselName: formState[SHIPMENT_DETAILS_CONSTANTS.VESSEL_NAME]?.value,
    containerNumber:
      formState[SHIPMENT_DETAILS_CONSTANTS.CONTAINER_NUMBER]?.value,
    modeOfTransportOtherDescription:
      formState[SHIPMENT_DETAILS_CONSTANTS.MODE_OF_TRANSPORT_OTHER_DESCRIPTION]
        ?.value,
    shipmentType:
      formState[SHIPMENT_DETAILS_CONSTANTS.SHIPMENT_TYPE]?.value?.value,
    billOfLadingNumber:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_NUMBER]?.value,
    billOfLadingDate:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_DATE]?.value,
    laneOrigination:
      formState[SHIPMENT_DETAILS_CONSTANTS.LANE_ORIGINATION]?.value,
    billOfLadingOriginationCity:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_ORIGINATION_CITY]
        ?.value,
    billOfLadingOriginationCountry:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_ORIGINATION_COUNTRY]
        ?.value?.value,
    laneDestination:
      formState[SHIPMENT_DETAILS_CONSTANTS.LANE_DESTINATION]?.value,
    billOfLadingDestinationCity:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_DESTINATION_CITY]
        ?.value,
    billOfLadingDestinationCountry:
      formState[SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_DESTINATION_COUNTRY]
        ?.value?.value,
    cargoDescription:
      formState[SHIPMENT_DETAILS_CONSTANTS.CARGO_DESCRIPTION]?.value,
    sealStatus: formState[SHIPMENT_DETAILS_CONSTANTS.SEAL_STATUS]?.value?.value,
    invoiceNumber: formState[SHIPMENT_DETAILS_CONSTANTS.INVOICE_NUMBER]?.value,
    invoiceCurrencyCode:
      formState[SHIPMENT_DETAILS_CONSTANTS.INVOICE_CURRENCY_CODE]?.value?.value,
    totalInvoiceValue: formatCurrencyStringToNumber(
      formState[SHIPMENT_DETAILS_CONSTANTS.TOTAL_INVOICE_VALUE]?.value
    ),

    //PAGE 3 INCIDENT DETAILS
    incidentDate: formState[INCIDENT_DETAILS_CONSTANTS.INCIDENT_DATE]?.value,
    typeOfClaim:
      formState[INCIDENT_DETAILS_CONSTANTS.TYPE_OF_CLAIM]?.value?.value,
    claimTypeId:
      formState[INCIDENT_DETAILS_CONSTANTS.CLAIM_TYPE_ID]?.value?.value,
    claimTypeDescription:
      formState[INCIDENT_DETAILS_CONSTANTS.CLAIM_TYPE_DESCRIPTION]?.value
        ?.label,
    totalClaimCurrencyCode:
      formState[INCIDENT_DETAILS_CONSTANTS.TOTAL_CLAIM_CURRENCY_CODE]?.value
        ?.value,
    totalClaimValue: formatCurrencyStringToNumber(
      formState[INCIDENT_DETAILS_CONSTANTS.TOTAL_CLAIM_VALUE]?.value
    ),
    expectedArrivalDate:
      formState[INCIDENT_DETAILS_CONSTANTS.EXPECTED_ARRIVAL_DATE]?.value,
    locationOfIncident:
      formState[INCIDENT_DETAILS_CONSTANTS.LOCATION_OF_INCIDENT]?.value,
    detailedIncidentSummary:
      formState[INCIDENT_DETAILS_CONSTANTS.DETAILED_INCIDENT_SUMMARY]?.value,
    policeReportAvailable:
      formState[INCIDENT_DETAILS_CONSTANTS.POLICE_REPORT_AVAILABLE]?.value
        ?.value ??
      formState[INCIDENT_DETAILS_CONSTANTS.POLICE_REPORT_AVAILABLE]?.value,
    cargoOrPropertyLocation:
      formState[INCIDENT_DETAILS_CONSTANTS.CARGO_OR_PROPERTY_LOCATION]?.value,
    contactName: formState[INCIDENT_DETAILS_CONSTANTS.CONTACT_NAME]?.value,
    contactPhoneNumber:
      formState[INCIDENT_DETAILS_CONSTANTS.CONTACT_PHONE_NUMBER]?.value,
    contactEmailAddress:
      formState[INCIDENT_DETAILS_CONSTANTS.CONTACT_EMAIL_ADDRESS]?.value,
    additionalInformation:
      formState[INCIDENT_DETAILS_CONSTANTS.ADDITIONAL_INFORMATION]?.value,
  };
  return createCargoClaimModel;
};
export const updateCargoClaimModelBuilder = (
  formState: FormStateType,
  additionalFields: CargoClaimAdditionalFields
): UpdateCargoClaimCommandInput => {
  const updateCargoClaimModel: UpdateCargoClaimCommandInput = {
    amazonClaimReferenceId: additionalFields.amazonClaimReferenceId,
    claimReporterFirstName: additionalFields?.user?.givenName || "",
    claimReporterLastName: additionalFields?.user?.familyName || "",
    claimReporterAlias: additionalFields?.user?.id || "",
    claimReporterJobDescription: additionalFields?.user?.jobDescription || "",
    claimNumber: additionalFields.claimNumber,
    incidentDate: additionalFields.incidentDate,
    quantity: formState[CLAIM_DETAILS_CONSTANTS.QUANTITY]?.value,
    handlingUnitIdentifier:
      formState[CLAIM_DETAILS_CONSTANTS.HANDLING_UNIT_IDENTIFIER]?.value?.value,
    weight: formState[CLAIM_DETAILS_CONSTANTS.WEIGHT]?.value,
    weightUnitType:
      formState[CLAIM_DETAILS_CONSTANTS.WEIGHT_UNIT_TYPE]?.value?.value,
    descriptionOfCargoOrProperty:
      formState[CLAIM_DETAILS_CONSTANTS.DESCRIPTION_OF_CARGO_OR_PROPERTY]
        ?.value,
    materialPartNumber:
      formState[CLAIM_DETAILS_CONSTANTS.MATERIAL_PART_NUMBER]?.value,
    amazonPartNumber:
      formState[CLAIM_DETAILS_CONSTANTS.AMAZON_PART_NUMBER]?.value,
    purchaseOrderNumber:
      formState[CLAIM_DETAILS_CONSTANTS.PURCHASE_ORDER_NUMBER]?.value,
    warehouseManagementSystemOrderNumber:
      formState[
        CLAIM_DETAILS_CONSTANTS.WAREHOUSE_MANAGEMENT_SYSTEM_ORDER_NUMBER
      ]?.value,
    amazonStandardIdentificationNumber:
      formState[CLAIM_DETAILS_CONSTANTS.AMAZON_STANDARD_IDENTIFICATION_NUMBER]
        ?.value,
    multipleItemClaimAttachment: formState[
      CLAIM_DETAILS_CONSTANTS.MULTIPLE_ITEM_CLAIM_ATTACHMENT
    ]?.value
      ? buildAttachments(
          formState[CLAIM_DETAILS_CONSTANTS.MULTIPLE_ITEM_CLAIM_ATTACHMENT]
            .value
        )
      : [],
  };
  return updateCargoClaimModel;
};

export const updateCargoClaimAttachmentsModelBuilder = (
  formState: FormStateType,
  additionalFields: CargoClaimAdditionalFields
): UpdateCargoClaimAttachmentsCommandInput => {
  const updateCargoClaimAttachmentsModel: UpdateCargoClaimAttachmentsCommandInput =
    {
      amazonClaimReferenceId: additionalFields.amazonClaimReferenceId,
      claimReporterFirstName: additionalFields?.user?.givenName || "",
      claimReporterLastName: additionalFields?.user?.familyName || "",
      claimReporterAlias: additionalFields?.user?.id || "",
      claimReporterJobDescription: additionalFields?.user?.jobDescription || "",
      claimNumber: additionalFields.claimNumber,
      incidentDate: additionalFields.incidentDate,

      commercialInvoice: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.COMMERCIAL_INVOICE]?.value || []
      ),
      billOfLadingFront: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.BILL_OF_LADING_FRONT]?.value || []
      ),
      billOfLadingReverse: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.BILL_OF_LADING_REVERSE]?.value ||
          []
      ),
      damagedPackagingPhotos: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.DAMAGED_PACKAGING_PHOTOS]?.value ||
          []
      ),
      damagedContentsPhotos: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.DAMAGED_CONTENTS_PHOTOS]?.value ||
          []
      ),
      proofOfDelivery: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.PROOF_OF_DELIVERY]?.value || []
      ),
      packingSlip: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.PACKING_SLIP]?.value || []
      ),
      proofFromCarrier: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.PROOF_FROM_CARRIER]?.value || []
      ),
      policeReport: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.POLICE_REPORT]?.value || []
      ),
      additionalDocuments: buildAttachments(
        formState[FILE_ATTACHMENTS_CONSTANTS.ADDITIONAL_DOCUMENTS]?.value || []
      ),
    };

  return updateCargoClaimAttachmentsModel;
};
